import React, { useRef, useEffect, useState } from "react";
import CheckIcon from "../../../assets/svg/check-icon";
import { BranchesSubDropdownProps } from "../CampaignBranchesDropdown";
import { visibleScrollbar } from "../../../utils/functions/get-visible-scrollbar";

const BranchesSubDropdown: React.FC<BranchesSubDropdownProps> = ({
  useCase,
  selectedBranches,
  onBranchToggle,
  onClose,
  className = "",
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const branches = useCase.branches || [];
  const selectedCount = selectedBranches.length;
  const totalCount = branches.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={dropdownRef}
      className={`
        absolute right-full top-0 -mr-1 w-[250px] bg-pure-white dark:bg-pure-white-dk
        border border-border-gray dark:border-border-gray-dk rounded-lg 
        shadow-popper-blur-effect backdrop-blur-[20px] z-[10000] overflow-hidden
        ${className}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1 border-b border-border-gray dark:border-border-gray-dk">
        <span className="text-[10px] font-semibold text-gray-placeholder dark:text-gray-placeholder-dk uppercase tracking-wide">
          Branches
        </span>
        <span className="text-[10px] font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
          {selectedCount}/{totalCount}
        </span>
      </div>

      {/* Branch List */}
      <div
        className={`max-h-[100px] overflow-y-auto ${visibleScrollbar(
          "[&::-webkit-scrollbar]:w-[6px]"
        )}`}
      >
        {branches.length === 0 ? (
          <div className="px-3 py-3 text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
            No branches available
          </div>
        ) : (
          branches.map((branch, index) => {
            const isSelected = selectedBranches.includes(branch.id);
            const isFocused = index === focusedIndex;

            return (
              <div
                key={branch.id}
                role="option"
                aria-selected={isSelected}
                className={`
                  flex items-center gap-1.5 cursor-pointer border-b border-border-gray dark:border-border-gray-dk 
                  last:border-b-0 transition-colors px-3 py-3 min-w-0
                  ${
                    isSelected
                      ? "bg-selected-option-highlight dark:bg-selected-option-highlight-dk border-b-selected-option-highlight-border dark:border-b-selected-option-highlight-border-dk"
                      : isFocused
                      ? "bg-hover-effect dark:bg-hover-effect-dk"
                      : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  }
                `}
                onClick={() => onBranchToggle(branch.id)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                {/* Checkbox */}
                <div
                  className={`shrink-0 w-[14px] h-[14px] border rounded-[3px] flex items-center justify-center ${
                    isSelected
                      ? "bg-verba-blue dark:bg-verba-blue-dk border-verba-blue dark:border-verba-blue-dk"
                      : "border-gray-placeholder dark:border-gray-placeholder-dk bg-popper-bg dark:bg-popper-bg-dk"
                  }`}
                >
                  {isSelected && (
                    <CheckIcon className="w-2.5 h-2.5 text-pure-white dark:text-pure-white-dk" />
                  )}
                </div>

                {/* Label */}
                <span className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk select-none">
                  {branch.name}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BranchesSubDropdown;